import * as z from 'zod';

/**
 * Search-service cities and their dispatch grid. A city owns a centre, an
 * "available" polygon and the hex-grid sizing; the grid is the set of boxes
 * around the centre, each with a price multiplier or blocked.
 *
 * int64 ids travel as strings, but a gateway may hand back numbers; both are
 * normalised to strings at the boundary.
 */
const idLike = z.union([z.string(), z.number()]).transform(String);

export const pointSchema = z.object({ lat: z.number(), long: z.number() });
export type Point = z.infer<typeof pointSchema>;

export const citySchema = z.object({
  id: idLike,
  name: z.string(),
  center: pointSchema,
  available_polygon: z
    .array(pointSchema)
    .nullish()
    .transform((v) => v ?? []),
  lat_km_per_degree: z.number().nullish(),
  long_km_per_degree: z.number().nullish(),
  hex_box_radius: z.number(),
});
export type City = z.infer<typeof citySchema>;

/** Write body shared by `POST /admin/cities` and `PUT /admin/cities/{id}`. */
export interface CityInput {
  name: string;
  center: Point;
  available_polygon: Point[];
  hex_box_radius: number;
}

export const gridBoxSchema = z.object({ x: z.number(), y: z.number() });
export type GridBox = z.infer<typeof gridBoxSchema>;

export const gridInitSchema = z.object({
  center: pointSchema,
  radius: z.number(),
});
export type GridInit = z.infer<typeof gridInitSchema>;

const gridMultiplierFields = z.object({
  location: pointSchema,
  multiplier: z
    .number()
    .nullish()
    .transform((v) => v ?? 0),
  blocked: z
    .boolean()
    .nullish()
    .transform((v) => v ?? false),
  city_id: idLike.nullish(),
  box: gridBoxSchema,
});
/** Accept the naming variants used by older delivery-service deployments. */
export const gridMultiplierSchema = z.preprocess((value) => {
  if (!value || typeof value !== 'object') return value;
  const row = value as Record<string, unknown>;
  if (row.blocked !== undefined) return value;
  return {
    ...row,
    blocked:
      row.is_blocked ??
      row.isBlocked ??
      (typeof row.status === 'string' && row.status === 'blocked'),
  };
}, gridMultiplierFields);
export type GridMultiplier = z.infer<typeof gridMultiplierSchema>;

/* --------------------------------- Form ---------------------------------- */

const required = { error: 'settings:errors.required' } as const;
const whole = (min: number) =>
  z
    .number(required)
    .int({ error: 'settings:errors.integer' })
    .min(min, { error: 'settings:errors.positive' });

const cityFormShape = {
  name: z.string().trim().min(1, { error: 'settings:errors.required' }),
  center_lat: z
    .number(required)
    .min(-90, { error: 'settings:cities.errors.lat' })
    .max(90, { error: 'settings:cities.errors.lat' }),
  center_long: z
    .number(required)
    .min(-180, { error: 'settings:cities.errors.long' })
    .max(180, { error: 'settings:cities.errors.long' }),
  hex_box_radius: whole(1),
  polygon: z
    .array(
      z.object({
        lat: z.number().min(-90).max(90),
        long: z.number().min(-180).max(180),
      }),
    )
    .max(100, { error: 'settings:cities.errors.polygon' })
    .refine((points) => points.length === 0 || points.length >= 3, {
      error: 'settings:cities.errors.polygon',
    }),
} as const;

/** Returns true when a geographic point is inside or on the polygon boundary. */
export function isPointInsidePolygon(point: Point, polygon: readonly Point[]): boolean {
  if (polygon.length < 3) return false;
  let inside = false;
  for (
    let index = 0, previous = polygon.length - 1;
    index < polygon.length;
    previous = index++
  ) {
    const current = polygon[index]!;
    const prior = polygon[previous]!;
    const cross =
      (current.long - prior.long) * (point.lat - prior.lat) -
      (current.lat - prior.lat) * (point.long - prior.long);
    const onEdge =
      Math.abs(cross) < 1e-10 &&
      point.long >= Math.min(prior.long, current.long) - 1e-10 &&
      point.long <= Math.max(prior.long, current.long) + 1e-10 &&
      point.lat >= Math.min(prior.lat, current.lat) - 1e-10 &&
      point.lat <= Math.max(prior.lat, current.lat) + 1e-10;
    if (onEdge) return true;
    const intersects =
      prior.lat > point.lat !== current.lat > point.lat &&
      point.long <
        ((current.long - prior.long) * (point.lat - prior.lat)) /
          (current.lat - prior.lat) +
          prior.long;
    if (intersects) inside = !inside;
  }
  return inside;
}

export const cityFormSchema = z.object(cityFormShape).superRefine((values, context) => {
  if (
    values.polygon.length >= MIN_POLYGON_POINTS &&
    !isPointInsidePolygon(
      { lat: values.center_lat, long: values.center_long },
      values.polygon,
    )
  ) {
    context.addIssue({
      code: 'custom',
      path: ['polygon'],
      message: 'settings:cities.errors.centerOutsidePolygon',
    });
  }
});
export type CityFormValues = z.infer<typeof cityFormSchema>;

/** A polygon needs 3+ vertices — or none, which the API reads as "no limit set". */
const MIN_POLYGON_POINTS = 3;

/** `null` when any line is malformed or the vertex count is 1–2. */
export function parsePolygon(text: string): Point[] | null {
  const lines = text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const points: Point[] = [];
  for (const line of lines) {
    const parts = line.split(/[\s,;]+/).filter(Boolean);
    if (parts.length !== 2) return null;
    const lat = Number(parts[0]);
    const long = Number(parts[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(long)) return null;
    if (Math.abs(lat) > 90 || Math.abs(long) > 180) return null;
    points.push({ lat, long });
  }
  if (points.length > 0 && points.length < MIN_POLYGON_POINTS) return null;
  return points;
}

export function formatPolygon(points: Point[]): string {
  return points.map((p) => `${p.lat}, ${p.long}`).join('\n');
}

export function cityToFormValues(city: City | null): CityFormValues {
  return {
    name: city?.name ?? '',
    center_lat: city?.center.lat ?? 41.2995,
    center_long: city?.center.long ?? 69.2401,
    hex_box_radius: city?.hex_box_radius ?? 0,
    polygon: city?.available_polygon ?? [],
  };
}

export function formValuesToCityInput(values: CityFormValues): CityInput {
  return {
    name: values.name.trim(),
    center: { lat: values.center_lat, long: values.center_long },
    available_polygon: values.polygon,
    hex_box_radius: values.hex_box_radius,
  };
}

/** The selected city lives in the URL so a grid view is linkable. */
export const citiesSearchSchema = z.object({
  city: z.coerce.string().optional().catch(undefined),
});
export type CitiesSearch = z.infer<typeof citiesSearchSchema>;
